// src/repo/wbsRepo.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getCurrentList,
    listAllVersions,
    listUnpublishedChanges,
    saveDraft,
    publishList,
    activateVersion,
    submitProgress,
    approveSubmission,
    rejectSubmission,
} from './api';
import type { WbsItemVersion } from './types';

export function useWbsCurrent(projectId: string, q: { viewAs: 'DATE' | 'WEEK'; at: Date }) {
    const atISO = q.at.toISOString();

    return useQuery({
        queryKey: ['wbs', 'current', projectId, q.viewAs, atISO],
        queryFn: () => getCurrentList(projectId, { viewAs: q.viewAs, at: atISO }),
        staleTime: 60_000,
        // placeholderData: (previousData) => previousData,
    });
}

export function useWbsVersions(projectId: string) {
    return useQuery({
        queryKey: ['wbs', 'versions', projectId],
        queryFn: () => listAllVersions(projectId),
        staleTime: 60_000,
    });
}
export function useWbsChanges(projectId: string) {
    return useQuery({
        queryKey: ['wbs', 'changes', projectId],
        queryFn: () => listUnpublishedChanges(projectId),
        staleTime: 30_000,
    });
}

export function useSaveDraft(projectId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ nodeId, patch }: { nodeId: string; patch: Partial<WbsItemVersion> }) =>
            saveDraft(projectId, nodeId, patch),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['wbs', 'current', projectId] }),
    });
}
export function usePublishList(projectId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (args: { draftVersionIds: string[]; title?: string; note?: string; activateNow?: boolean }) =>
            publishList(projectId, args.draftVersionIds, {
                title: args.title,
                note: args.note,
                activateNow: args.activateNow,
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['wbs', 'current', projectId] });
            qc.invalidateQueries({ queryKey: ['wbs', 'versions', projectId] });
            qc.invalidateQueries({ queryKey: ['wbs', 'changes', projectId] });
        },
    });
}
export function useActivateVersion(projectId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (version: number) => activateVersion(projectId, version),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['wbs', 'current', projectId] });
            qc.invalidateQueries({ queryKey: ['wbs', 'versions', projectId] });
        },
    });
}
export function useProgressActions(projectId: string) {
    const qc = useQueryClient();

    const submitMut = useMutation({
        mutationFn: (args: { nodeId: string; weekOf: string; evPct: number; listVersionUsed?: number }) =>
            submitProgress(projectId, args.nodeId, args.weekOf, args.evPct, args.listVersionUsed),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['wbs', 'current', projectId] }),
    });

    const approveMut = useMutation({
        mutationFn: (submissionId: string) => approveSubmission(projectId, submissionId),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['wbs', 'current', projectId] }),
    });

    const rejectMut = useMutation({
        mutationFn: (args: { submissionId: string; reason?: string; expectedEarnedValue: number }) =>
            rejectSubmission(projectId, args.submissionId, {
                reason: args.reason,
                expectedEarnedValue: args.expectedEarnedValue,
            }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['wbs', 'current', projectId] }),
    });

    return {
        // WbsListView가 기대하는 시그니처로 래핑
        submit: (args: { nodeId: string; weekOf: string; evPct: number; listVersionUsed?: number }) =>
            submitMut.mutateAsync(args),

        // 다중 승인 지원
        approve: async (progressIds: string[]) => {
            await Promise.all(progressIds.map((id) => approveMut.mutateAsync(id)));
        },

        // 다중 반려 지원
        reject: async (payload: { progressIds: string[]; note?: string }) => {
            await Promise.all(
                payload.progressIds.map((id) =>
                    rejectMut.mutateAsync({
                        submissionId: id,
                        reason: payload.note,
                        expectedEarnedValue: 0, // ✅ 임시 기본값(필요시 UI에서 받아서 넣도록 확장)
                    }),
                ),
            );
        },

        // 로딩 플래그 3종 추가 노출
        isSubmitting: submitMut.isPending,
        isApproving: approveMut.isPending,
        isRejecting: rejectMut.isPending,
    };
}
